<div class="flex flex-col gap-8">
  <h2 class="text-2xl font-bold text-gray-12">Quel est votre projet ?</h2>

  <!-- Seller Role Selection -->
  <div class="flex flex-col gap-4">
    <span class="block text-sm font-medium text-gray-11"
      >Votre statut par rapport au bien <span class="text-ruby-11">*</span></span
    >

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      @for (sellerRole of sellerRoles; track sellerRole.role) {
        <aura-button-card
          [label]="sellerRole.label"
          [selected]="model().seller.role === sellerRole.role"
          (clicked)="selectSellerRole(sellerRole.role)"
        >
          <mat-icon icon [svgIcon]="sellerRole.icon" />
        </aura-button-card>
      }
    </div>

    <!-- Non-owner extra details -->
    @if (isNonOwner()) {
      <div
        class="mt-2 flex flex-col gap-5 rounded-2xl border border-neutral-3 bg-neutral-2/40 p-5 md:p-6 transition-all"
      >
        <!-- Case 1: Family / Close relative -->
        @if (isFamily()) {
          <div class="flex items-center gap-2 text-sm font-semibold text-neutral-12">
            <mat-icon class="text-primary-9" svgIcon="users" />
            <span>Informations sur le propriétaire représenté</span>
          </div>

          <div class="flex flex-col gap-4">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>Prénom du propriétaire</mat-label>
                <input
                  matInput
                  [formField]="formTree.seller.beneficiary.firstName"
                  placeholder="Ex : Marie"
                />
              </mat-form-field>

              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>Nom du propriétaire</mat-label>
                <input
                  matInput
                  [formField]="formTree.seller.beneficiary.lastName"
                  placeholder="Ex : DUPONT"
                />
              </mat-form-field>
            </div>

            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>Email du propriétaire </mat-label>
                <input
                  matInput
                  type="email"
                  [formField]="formTree.seller.beneficiary.email"
                  placeholder="Ex : marie.dupont@example.com"
                />
              </mat-form-field>

              <aura-phone-number-input
                [control]="phoneControl"
                [required]="false"
                label="Téléphone du propriétaire"
                (numberChanged)="onPhoneChanged($event)"
              />
            </div>
          </div>

          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Votre lien avec le propriétaire </mat-label>
            <input
              matInput
              [formField]="formTree.seller.representationDetails"
              placeholder="Ex : Fils aîné, Fille, Proche aidant..."
            />
          </mat-form-field>
        }

        <!-- Case 2: Company / SCI -->
        @if (isCompany()) {
          <div class="flex items-center gap-2 text-sm font-semibold text-neutral-12">
            <mat-icon class="text-primary-9" svgIcon="building-2" />
            <span>Informations sur la société propriétaire</span>
          </div>

          <div class="flex flex-col gap-4">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>Dénomination sociale / Nom de la société</mat-label>
                <input
                  matInput
                  [formField]="formTree.seller.beneficiary.companyName"
                  placeholder="Ex : SCI Les Oliviers"
                />
              </mat-form-field>

              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>Numéro SIREN </mat-label>
                <input
                  matInput
                  [formField]="formTree.seller.beneficiary.siren"
                  placeholder="Ex : 123 456 789"
                />
              </mat-form-field>
            </div>
          </div>

          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Votre fonction dans la société </mat-label>
            <input
              matInput
              [formField]="formTree.seller.representationDetails"
              placeholder="Ex : Gérant, Associé majoritaire..."
            />
          </mat-form-field>
        }

        <!-- Case 3: Estate / Heir -->
        @if (isEstateHeir()) {
          <div class="flex items-center gap-2 text-sm font-semibold text-neutral-12">
            <mat-icon class="text-primary-9" svgIcon="landmark" />
            <span>Informations sur la succession ou l'indivision</span>
          </div>

          <div class="flex flex-col gap-4">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>Nom de la succession ou des indivisaires</mat-label>
                <input
                  matInput
                  [formField]="formTree.seller.beneficiary.estateName"
                  placeholder="Ex : Succession Pierre DUPONT ou Indivision DUPONT"
                />
              </mat-form-field>

              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>Notaire en charge de la succession </mat-label>
                <input
                  matInput
                  [formField]="formTree.seller.beneficiary.notaryName"
                  placeholder="Ex : Me Laurent, Bayonne"
                />
              </mat-form-field>
            </div>
          </div>

          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Votre rôle dans l'indivision </mat-label>
            <input
              matInput
              [formField]="formTree.seller.representationDetails"
              placeholder="Ex : Héritier mandaté, Co-indivisaire..."
            />
          </mat-form-field>
        }

        <div class="pt-1">
          <mat-checkbox [formField]="formTree.seller.hasOwnerConsent" class="primary">
            <span class="text-xs text-neutral-12"
              >Je certifie agir en plein accord avec les propriétaires légaux ou co-indivisaires
              <span class="text-ruby-11">*</span></span
            >
          </mat-checkbox>
        </div>
      </div>
    }
  </div>

  <div class="h-px w-full bg-gray-2"></div>

  <!-- Offer Type Selection -->
  <div class="flex flex-col gap-4">
    <span class="block text-sm font-medium text-gray-11"
      >Type d'offre <span class="text-ruby-11">*</span></span
    >

    <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
      @for (offer of offerTypes; track offer.type) {
        <aura-button-card
          [label]="offer.label"
          [selected]="model().offerType === offer.type"
          (clicked)="selectOfferType(offer.type)"
        >
          <mat-icon icon [svgIcon]="offer.icon" />
        </aura-button-card>
      }
    </div>
  </div>

  @if (model().offerType) {
    <div class="h-px w-full bg-gray-2"></div>

    <!-- Property Type Selection -->
    <div class="flex flex-col gap-4">
      <span class="block text-sm font-medium text-gray-11"
        >Type de bien <span class="text-ruby-11">*</span></span
      >

      <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
        @for (type of propertyTypes; track type.type) {
          <aura-button-card
            [label]="type.label"
            [selected]="model().propertyType === type.type"
            (clicked)="selectType(type.type)"
          >
            <mat-icon icon [svgIcon]="type.icon" />
          </aura-button-card>
        }
      </div>
    </div>
  }

  @if (isResidential()) {
    <div class="h-px w-full bg-gray-2"></div>

    <!-- Residence Type Selection -->
    <div class="flex flex-col gap-4">
      <span class="block text-sm font-medium text-gray-11"
        >Usage actuel du bien <span class="text-ruby-11">*</span></span
      >

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
        @for (residence of residenceTypes; track residence.type) {
          <aura-button-card
            [label]="residence.label"
            [selected]="model().residenceType === residence.type"
            (clicked)="selectResidenceType(residence.type)"
          >
            <mat-icon icon [svgIcon]="residence.icon" />
          </aura-button-card>
        }
      </div>
    </div>
  }
</div>
